import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import axios, { AxiosError } from 'axios'
import {
  RequestTimeoutException,
  ConnectTimeoutException,
  HttpAuthorizationException,
  HttpResourceNotFoundException,
  HttpServerException,
  HttpLocalCancelException,
  HttpRemoteCancelException,
} from '@core/Exceptions'
import { TimeConstants } from '@core/Constants'
import { isMappReloginDisabled } from '@/DebugConfig'

const UPLOAD_REQUEST_RETRIES_TIMES = 0

const UPLOAD_TIMEOUT_IN_MSEC = 60 * 60 * 1000 // set to 60 mins for upload

// export for testing purposes
export const REQUEST_TIMEOUT_IN_MSEC = 5 * 1000

// const REQUEST_TIMEOUT_IN_MSEC = 10 * 1000 // temp set to 10 sec for integration convenience

const RESPONSE_TIMEOUT_ERROR = 504
const RESPONSE_SERVICE_ERROR_ARRAY = [500, 501, 502, 503, 505]
const RESPONSE_AUTHORIZATION_ERROR = 403
const RESPONSE_PAGE_NOT_FOUND_ERROR = 404
const CONNECT_TIMEOUT_ERROR_STATUS = 408
const NETWORK_AUTHENTICATION_REQUIRED = 511

export const HTTP_STATUS_OK = 200

export const configWithUpload = {
  timeout: UPLOAD_TIMEOUT_IN_MSEC,
  'axios-retry': {
    retries: UPLOAD_REQUEST_RETRIES_TIMES,
  },
}

export interface RequestConfig {
  /**
   * timeout in milliseconds
   */
  timeout?: number
}

export abstract class AbsNetAdapter<T> {
  protected _instance: AxiosInstance

  constructor() {
    this._instance = axios.create({
      baseURL: this._getBaseUrl(),
      timeout: REQUEST_TIMEOUT_IN_MSEC,
      withCredentials: this._getWithCredentialsOption(),
    })
  }

  protected _initInstance() {
    if (this._isRetryEnabled()) {
      axiosRetry(this._instance, {
        retries: TimeConstants.DEFAULT_RETRY_TIMES,
        shouldResetTimeout: true,
        retryCondition: (error) =>
          error.code !== AxiosError.ERR_CANCELED &&
          (this._isNetworkOrIdempotentRequestError(error) || error.code === AxiosError.ECONNABORTED),
        retryDelay: (retryCount, error) => axiosRetry.exponentialDelay(retryCount),
        onRetry: (retryCount, error, requestConfig) => {
          const previousTimeout = requestConfig?.timeout ?? REQUEST_TIMEOUT_IN_MSEC

          requestConfig.timeout = previousTimeout * TimeConstants.DEFAULT_RETRY_TIMEOUT_FACTOR
          this._onRetry(retryCount, error, requestConfig)
        },
      })
    }

    this._instance.interceptors.request.use(
      (config) => this._interceptRequest(config),
      (error) => {
        const { request } = error

        if (request && request.status === CONNECT_TIMEOUT_ERROR_STATUS) throw new ConnectTimeoutException()

        return Promise.reject(error)
      }
    )

    this._instance.interceptors.response.use(
      (res) => this._interceptFulfillResponse(res),
      (error) => {
        const { response } = error

        if (response) {
          switch (response.status) {
            case RESPONSE_AUTHORIZATION_ERROR:
              if (isMappReloginDisabled()) break
              throw new HttpAuthorizationException()
            case RESPONSE_PAGE_NOT_FOUND_ERROR:
              throw new HttpResourceNotFoundException()
            case RESPONSE_TIMEOUT_ERROR:
              throw new RequestTimeoutException()

            default:
              if (RESPONSE_SERVICE_ERROR_ARRAY.includes(response.status)) throw new HttpServerException()
              else break
          }
        } else if (error.code === AxiosError.ECONNABORTED) {
          // being blocked without response data
          throw new RequestTimeoutException()
        } else if (error.code === AxiosError.ERR_CANCELED) {
          if (this._isCancelLocally(error)) {
            throw new HttpLocalCancelException()
          } else {
            throw new HttpRemoteCancelException()
          }
        }

        return Promise.reject(error)
      }
    )
  }

  protected _isRetryEnabled(): boolean {
    return true
  }

  protected _isHttpOk(response: AxiosResponse<T>) {
    return response.status === HTTP_STATUS_OK
  }

  protected _isCancelLocally(error: unknown) {
    return axios.isCancel(error)
  }

  protected _isSuccess(response: AxiosResponse<T>) {
    return this._isHttpOk(response)
  }

  protected abstract _getBaseUrl(): string

  protected _interceptRequest(config: AxiosRequestConfig) {
    return config
  }

  protected _interceptFulfillResponse(res: AxiosResponse) {
    return Promise.resolve(res)
  }

  protected abstract _getWithCredentialsOption(): boolean

  // isNetworkOrIdempotentRequestError 包含错误码511(权限错误)，理应排除，复写该方法
  private _isNetworkOrIdempotentRequestError(error: AxiosError): boolean {
    if (axiosRetry.isNetworkOrIdempotentRequestError(error)) {
      return !error.response || error.response.status !== NETWORK_AUTHENTICATION_REQUIRED
    }

    return false
  }

  protected _onRetry(retryCount: number, error: AxiosError, config: AxiosRequestConfig) {
    // do nothing, used for child class if needed
  }
}
